/** Produkt-Registry des Brokers-Tabs — die EINE Stelle für Reihenfolge, Beschriftung und
 *  Conversion-Stufen je Produkt. Kachelreihe der Werbekonten und Conversions-Reihe lesen
 *  beide hier (Muster wie `accounts.ts` für die Microsoft-Konten).
 *
 *  slug = Broker-Schlüssel des Trackers (OPENAI_ACCOUNTS, conversions.broker). Nur Produkte
 *  mit eigenem Tracking stehen hier — Pepperstone/Apollo zeigt das Dashboard nicht mehr
 *  (Operator 2026-09-10). */

export type StageIcon = "signup" | "deposit" | "party" | "sale" | "volume";

export interface ProductStage {
  /** Tracker event_stage (registration | ftd | cpa | sale) */
  key: string;
  /** Beschriftung der Conversions-Kachel */
  label: string;
  /** Kurzform für die Tracker-Fußnote der Konto-Kachel („3 Reg · 1 FTD") */
  short: string;
  icon: StageIcon;
  /** Kurzform für die schmale „Other Conversions"-Karte, damit die Beschriftung in EINE
   *  Zeile passt („Trader" statt „Trader (CPA)"). Ohne Angabe gilt `label`. Die breite
   *  eToro-Karte nimmt immer `label`. */
  labelCompact?: string;
  /** Seltene Stufe: Kachel nur zeigen, wenn > 0 */
  hideIfZero?: boolean;
  /** Diese Stufe trägt die Provision des Produkts (Summe Payout) als Unterzeile */
  provision?: boolean;
  /** Vorstufe, die als Zusatzzeile in dieser Kachel steht, aber NICHT mitzählt (BlackBull:
   *  Cellxpert-"Lead" = nur E-Mail eingegeben, Sign-up ist erst die fertige Registrierung). */
  vorstufe?: { key: string; label: string; short: string };
}

export type Org = "A" | "B" | "C" | "D";
/** Reihenfolge der Kachelreihen. */
export const ORGS: Org[] = ["A", "B", "C", "D"];

export interface Product {
  slug: string;
  label: string;
  rank: number;
  stages: ProductStage[];
  /** Im Dashboard ausgeblendet (Kachel + Conversions), Tracking läuft weiter (Operator 2026-09-11) */
  hidden?: boolean;
  /** Fasst die Conversions aller Werbekonten derselben Marke zusammen (Slug-Präfix, also
   *  etoro+etoro2…etoro9 bzw. avatrade+avatrade2). Der Leser interessiert sich fuer die Marke,
   *  nicht dafuer, aus welchem Werbekonto der Klick kam — die Kacheln bleiben getrennt. */
  mergesAccounts?: boolean;
  /** Zählt ALLE Quellen, nicht nur Klicks aus OpenAI-Kampagnen. Für RoboForex: die
   *  Partnergruppe gehört komplett uns, jede Registrierung darin stammt aus unserem
   *  Agentenlink — nur WELCHER Klick es war, lässt sich mangels Klick-ID oft nicht
   *  eindeutig sagen (Zeitfenster mehrdeutig). Mit Quellenfilter stünde dort 1 statt 30. */
  allSources?: boolean;
  /** OpenAI-Organisation ("Masterkonto") des Werbekontos, je hoechstens zehn Konten. A = erste
   *  Organisation, B ab eToro 9, C ab eToro 17. Jedes Masterkonto beginnt eine eigene Kachelreihe. */
  org?: Org;
  /** Eigene Spalte im Brokers-Tab (Conversions oben, Werbekonten darunter). Reihenfolge = rank. Operator
   *  2026-09-30: AvaTrade plus je eine Spalte fuer jedes neue Produkt (Masterkonto D), BlackBull raus. */
  spalte?: boolean;
  /** Haupt-Einnahmequelle (Operator 2026-10-01: eToro ueber Microsoft, AvaTrade ueber ChatGPT): grosse Karte in der
   *  obersten Reihe. Alle anderen Spalten-Produkte sind Tests und stehen kompakt darunter. */
  haupt?: boolean;
  /** Kartenreihe der Tests (Operator 2026-10-09): 2 = 1xBet, eToro · Google; 3 = RoboForex, Deel, OpenAI · Top 10, Pepperstone.
   *  Ohne Angabe Reihe 2. */
  reihe?: 2 | 3;
  /** Markenname als Kartentitel, wenn `label` ein Werbekonto bezeichnet ("eToro 1" -> "eToro"). */
  marke?: string;
  /** Zusaetzliche Werbekonten der Marke ausserhalb von OpenAI: Microsoft-Konten aus `accounts.ts`. */
  microsoft?: boolean;
  /** Eigene Microsoft-Kampagnen in einem Sammelkonto (RoboForex-Relaunch 2026-10-01: DL - $ 138225870, Kampagnen
   *  mit Namensanfang `msPrefix`). Ergibt eine Konto-Kachel nur mit diesen Kampagnen. */
  msKonto?: string;
  msPrefix?: string;
  /** Anzeigename des Sammelkontos, falls Microsoft die (neuen) Kampagnen noch nicht im Bericht hat. */
  msKontoName?: string;
  /** Weitere Microsoft-Konten mit denselben Kampagnen (gleicher `msPrefix`), je Konto eine eigene Kachel `name`
   *  (1xBet 2026-10-12: Kopie aller 1X-Kampagnen im Konto $ - b/c als "1xBet 2"). */
  msZusatz?: { konto: string; name: string }[];
  /** Interne Schaetzung des Werts je Sign-up nach Land (Klick-Land, Kleinbuchstaben) fuer die Wertzeile unter "Sign-ups"
   *  (Operator 2026-10-03: Tageswert = Sign-ups x Schaetzung). `mitSpread` nur fuer den Tooltip.
   *  `geraet`: Faktor je Geraet des Klicks (desktop/ios/android/unknown, Operator 2026-10-05) - `werte` gelten fuer Desktop. */
  regWert?: {
    werte: Record<string, number>; standard: number; mitSpread?: Record<string, number>; hinweis: string;
    geraet?: Record<string, number>;
    /** Faktor je Klickquelle (AvaTrade 2026-10-07: `werte` sind fuer ChatGPT-Verkehr x0,53 gerechnet, Microsoft-Suche nicht). */
    quelle?: Record<string, number>;
  };
  /** Broker-Praefix der Conversions, falls er vom Slug abweicht (eToro ueber Google: Slug "etoro-google", Broker "etoro"). */
  broker?: string;
  /** Google-Ads-Konto (Top 10 Trading Platforms, /api/google/campaigns) als Konto-Kachel der Marke. */
  google?: boolean;
  /** Eigenes Google-Ads-Konto der Marke (Tracker /stats/google/campaigns?konto=<wert>, Operator 2026-11-04: Palmera-PMax
   *  im Konto 2649797403) als zusaetzliche Konto-Kachel; Leads/Deals kommen ueber die gclid aus der Partner-API. */
  googleKonto?: string;
  /** Meta-Werbekonto (Facebook/Instagram, /api/meta/campaigns) als Konto-Kachel der Marke (Operator 2026-10-13). */
  meta?: boolean;
  /** Nur Meta-Kampagnen, deren Name passt (seit 2026-10-26 mehrere Marken im selben Meta-Konto: AvaTrade, NordVPN). */
  metaKampagne?: RegExp;
  /** Reddit-Werbekonto (/api/reddit/campaigns) als Konto-Kachel der Marke (Operator 2026-10-15). */
  reddit?: boolean;
  /** MGID-Werbekonto (/api/mgid/campaigns) als Konto-Kachel der Marke (Operator 2026-10-16). */
  mgid?: boolean;
  /** Taboola-Werbekonto (/api/taboola/campaigns) als Konto-Kachel der Marke (Operator 2026-10-16). */
  taboola?: boolean;
  /** Welche Klickquellen als Conversion zaehlen (statt nur "openai"). RoboForex (Operator 2026-10-01): nur neue Kunden
   *  ueber UNSERE Klicks - Konten ohne Klick sind meist Zusatzkonten bestehender Kunden und zaehlen nicht. */
  quellen?: string[];
  /** Conversions ALLER Klickquellen ausser diesen zaehlen (eToro · Microsoft, Operator 2026-10-06: alles ausser Google,
   *  damit z. B. FTDs/Trader frueherer ChatGPT-Anmeldungen in derselben Karte stehen). Konto-Kacheln und Kostenbasis
   *  bleiben bei `quellen`. */
  quellenAusser?: string[];
  /** Nur Microsoft-Conversions aus den eigenen Kampagnen der Karte zaehlen (eToro · Microsoft, Operator 2026-11-03):
   *  eToro-Anmeldungen aus fremden Kampagnen (z. B. "$5 · FR | ava-first (8.10)") bleiben draussen. */
  nurEigeneKampagnen?: boolean;
  /** Karte mit mehreren Zielmarken (Operator 2026-10-06, "OpenAI · Top 10 Vergleich"): je Marke eine Zeile mit den
   *  Stufen des Produkts. `praefix` = Tracker-Broker der Zeile (etorovgl -> etorovgl, etorovgl1, etorovgl2), `marke` = Logo. */
  vergleich?: { marke: string; label: string; praefix: string }[];
}

export const PRODUCTS: Product[] = [
  {
    // Seit 2026-10-01 wieder Haupt-Einnahmequelle ueber Microsoft ($1-$5); die eToro-Konten bei OpenAI sind pausiert.
    // Alle Quellen zaehlen (die Chips zeigen Microsoft, MS ohne ID, OpenAI getrennt).
    // Seit 2026-10-02 nach Kanal getrennt (Operator): Google hat eine eigene Karte. Seit 2026-10-06 zaehlen hier die
    // Conversions aller Quellen ausser Google (Klicks ohne Kennung); Kacheln und Kosten bleiben Microsoft.
    // Seit 2026-11-03 (Operator) ohne OpenAI und ohne Microsoft-Conversions aus fremden Kampagnen ($5 ava-first).
    slug: "etoro", label: "eToro 1", marke: "eToro · Microsoft", rank: 1, mergesAccounts: true, spalte: true, haupt: true,
    microsoft: true, quellen: ["microsoft", "microsoft-noid"], nurEigeneKampagnen: true,
    quellenAusser: ["google", "google-noid", "meta", "meta-noid", "reddit", "mgid", "taboola", "openai", "openai-noid"],
    // Wert je Microsoft-Registrierung (05.10., etoro-ltv/analysis-2026-10-03/wert_kurzfenster.py): Trader-Quote der
    // Microsoft-Anmeldungen seit Juni in 7 und 30 Tagen (Mitte beider Basen), mit dem Reifeverlauf 2025 auf ein Jahr
    // hochgerechnet, Laendermuster aus der eToro-Historie, heutige CPA (Tier 1 750 $, ES 650 $, AU/DK/CZ 500 $).
    // Geraet (Analyse 05.10.): ChatGPT-Anmeldungen vom iPhone ~0,65x, von Android ~0,25x Desktop.
    regWert: {
      werte: { uk: 52, gb: 52, ie: 54, ch: 53, au: 51, de: 49, nl: 47, se: 46, fr: 41, no: 38, cz: 30, it: 29, at: 28, es: 22, dk: 8 },
      standard: 35,
      geraet: { desktop: 1, ios: 0.65, android: 0.25, unknown: 1 },
      hinweis: "Sign-ups x Wert je Land x Gerät. Wert je Land = Trader-Quote der Microsoft-Anmeldungen seit Juni (7 und 30 Tage, aufs Jahr hochgerechnet) x heutiger CPA (750 $, ES 650 $, AU/DK 500 $). Gerät: Desktop 1, iPhone 0,65, Android 0,25. Faktor = Wert / Ausgaben der Marke.",
    },
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
      { key: "cpa", label: "Trader (CPA)", short: "Trader", icon: "party" },
    ],
  },
  {
    /** eToro ueber Google Ads (Operator 2026-10-02): Konto Top 10 Trading Platforms, Zielseiten /de/… und /fr/…;
     *  zaehlt nur Google-Klicks. Steht vorerst in der zweiten Reihe neben RoboForex und Deel. */
    slug: "etoro-google", broker: "etoro", label: "eToro · Google", marke: "eToro · Google", rank: 1.5,
    spalte: true, google: true, quellen: ["google", "google-noid"],
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
      { key: "cpa", label: "Trader (CPA)", labelCompact: "Trader", short: "Trader", icon: "party" },
    ],
  },
  {
    /** eToro ueber Facebook/Instagram (Operator 2026-10-13): Meta-Konto "Top 10", zwei Kampagnen (Desktop, Mobil) auf
     *  /fr/comparatif/. Die Kampagnen optimieren auf eToro-Klicks (Pixel "Lead"); hier zaehlen trotzdem NUR echte
     *  Registrierungen/FTDs aus dem Tracker (Klickquelle meta). Steht neben eToro · Google.
     *  16.10. ausgeblendet (alle Meta-Kampagnen pausiert); seit 2026-10-22 wieder an (Operator): 10 Funnel-Seiten /fr/investir-(...)/,
     *  Kampagnen "FR | Meta | Funnels W1/W2 | eToro-Klick" (10 Anzeigengruppen, je 10 $/Tag, nur iPhone). */
    slug: "etoro-meta", broker: "etoro", label: "eToro · Facebook", marke: "eToro · Facebook", rank: 1.55,
    // 2026-10-26 (Operator): alle eToro-Meta-Kampagnen sind seit 24.10. pausiert und sollen nicht mehr gezeigt werden; die
    // Facebook-Kachel haengt jetzt an "AvaTrade · Facebook". Wieder einblenden: hidden entfernen.
    // 2026-10-30 (Operator): wieder an fuer die neue Kette etoro.signup-here.com -> top10 /etoro-review/ -> eToro-Partnerlink,
    // Kampagne "eToro | Meta | Registrierung FR" (iPhone + Desktop, je 30 $/Tag, Ziel Registrierung). Facebook-Kachel nur mit
    // den eToro-Kampagnen (metaKampagne), Conversions = Tracker, Quelle meta, Broker etoro.
    // 2026-10-30 (Operator): Kampagne am selben Tag pausiert, Karte wieder raus aus dem Dashboard.
    spalte: true, meta: true, metaKampagne: /eToro/i, quellen: ["meta", "meta-noid"], hidden: true,
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
      { key: "cpa", label: "Trader (CPA)", labelCompact: "Trader", short: "Trader", icon: "party" },
    ],
  },
  {
    /** eToro ueber Reddit Ads (Operator 2026-10-15): 4 Kampagnen "Top10 <LAND> | Trading-Plattformen 2026" (UK, DE, FR, AU,
     *  Desktop) auf die Laenderseiten /uk-trading/, /de/, /fr/comparatif/, /au-trading/; tracker.js reicht Reddits rdt_cid an /go.
     *  Kosten/Klicks ueber die Reddit-Ads-API (Konto-Kachel "Reddit"), Registrierungen nur aus dem Tracker (Quelle reddit).
     *  16.10. kurz ausgeblendet; seit 2026-10-16 abends wieder an (Operator): 4 Kampagnen im neuen Konto ataraxgmbh (EUR).
     *  Partner heisst "Top 10 Vergleich" wie bei MGID/Taboola: der Verkehr geht auf die Top-10-Laenderseiten. */
    slug: "etoro-reddit", broker: "etoro", label: "Top 10 Vergleich · Reddit", marke: "Top 10 Vergleich · Reddit", rank: 1.6, hidden: true,   // Operator 2026-10-24: pausiert, raus aus dem Dashboard
    spalte: true, reddit: true, quellen: ["reddit"],
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
      { key: "cpa", label: "Trader (CPA)", labelCompact: "Trader", short: "Trader", icon: "party" },
    ],
  },
  {
    /** eToro ueber MGID Native (Operator 2026-10-16): 2 Kampagnen "Top10 | DE/AT | Desktop | Finanz-Allowlist" auf /de/ und
     *  /at-trading/ (nur Finanz- und Qualitaetsseiten). Kosten/Klicks ueber die MGID-API (Konto-Kachel "MGID", EUR),
     *  Registrierungen nur aus dem Tracker (Quelle mgid = mgclid in der Landing-URL).
     *  Partner heisst "Top 10 Vergleich" (Operator 2026-10-16: der Verkehr geht auf die Top-10-Seite, nicht direkt zu eToro). */
    slug: "etoro-mgid", broker: "etoro", label: "Top 10 Vergleich · MGID", marke: "Top 10 Vergleich · MGID", rank: 1.7, hidden: true,   // Operator 2026-10-24: pausiert, raus aus dem Dashboard
    spalte: true, mgid: true, quellen: ["mgid"],
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
      { key: "cpa", label: "Trader (CPA)", labelCompact: "Trader", short: "Trader", icon: "party" },
    ],
  },
  {
    /** eToro ueber Taboola Native (Operator 2026-10-16, eToro-Freigabe): Kampagnen "Top10 | DE/AT | Desktop" auf /de/ und /at-trading/.
     *  Kosten/Klicks ueber die Taboola-Backstage-API (Konto-Kachel "Taboola", EUR), Registrierungen nur aus dem Tracker (Quelle taboola = tblci).
     *  Partner heisst "Top 10 Vergleich" (Operator 2026-10-16, wie MGID). */
    slug: "etoro-taboola", broker: "etoro", label: "Top 10 Vergleich · Taboola", marke: "Top 10 Vergleich · Taboola", rank: 1.8, hidden: true,   // Operator 2026-10-24: pausiert, raus aus dem Dashboard
    spalte: true, taboola: true, quellen: ["taboola"],
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
      { key: "cpa", label: "Trader (CPA)", labelCompact: "Trader", short: "Trader", icon: "party" },
    ],
  },
  {
    /** Zweites eToro-OpenAI-Konto (Landing /etoro-registrations-2/); Stufen folgen mit dem Gating. */
    slug: "etoro2", label: "eToro 2", rank: 2,
    stages: [],
  },
  { slug: "etoro3", label: "eToro 3", rank: 3, stages: [] },
  { slug: "etoro4", label: "eToro 4", rank: 4, stages: [] },
  { slug: "etoro5", label: "eToro 5", rank: 5, stages: [] },
  { slug: "etoro6", label: "eToro 6", rank: 6, stages: [] },
  { slug: "etoro7", label: "eToro 7", rank: 7, stages: [] },
  { slug: "etoro8", label: "eToro 8", rank: 8, stages: [] },
  /** Erstes Konto der zweiten Organisation ("Konto B"), eigene Kachelreihe. */
  { slug: "etoro9", label: "eToro 9", rank: 9, stages: [], org: "B" },
  /** eToro 10-16 fuellen Masterkonto B auf (Pixel seit 2026-09-24). Rang 9.1-9.7: direkt
   *  hinter eToro 9, vor AvaTrade 2 und RoboForex 2. */
  ...[10, 11, 12, 13, 14, 15, 16].map((n): Product => (
    { slug: `etoro${n}`, label: `eToro ${n}`, rank: 9 + (n - 9) / 10, stages: [], org: "B" })),
  {
    /** AvaTrade ueber Meta Ads (Operator 2026-10-26): Anzeigen -> avatrade.signup-here.com (fbclid-Weiche) -> top10 /avatrade-review/
     *  (Tracker-Klick broker avatrade, Quelle meta) -> AvaPartner je Land. Kampagne "AvaTrade | Meta | Registrierung DE+FR",
     *  4 Anzeigengruppen (DE/FR x iPhone/Desktop, je 10 $/Tag), Ziel = Registrierung (Custom Conversion aus dem AvaPartner-Postback
     *  per CAPI). Facebook-Kachel (meta: true) = aktive Kampagnen des Meta-Kontos; Conversions = Tracker, Quelle meta. */
    slug: "avatrade-meta", broker: "avatrade", label: "AvaTrade · Facebook", marke: "AvaTrade · Facebook", rank: 11.05,
    spalte: true, meta: true, metaKampagne: /AvaTrade/i, quellen: ["meta", "meta-noid"], hidden: true,   // Operator 2026-10-31: Kampagne pausiert, raus aus dem Dashboard
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
      { key: "cpa", label: "Trader (CPA)", labelCompact: "Trader", short: "Trader", icon: "party" },
    ],
  },
  {
    /** AvaTrade (ab 2026-09-15 mit eigenem Gating + AvaPartner-Postback): drei Stufen wie eToro,
     *  Anzeige in der „Other Conversions"-Liste mit Länder-Chips, nur OpenAI-Klicks. */
    slug: "avatrade", label: "AvaTrade", rank: 11, mergesAccounts: true, spalte: true, haupt: true,
    // Seit 2026-10-07 zusaetzlich Microsoft: Konto $5 testet die Seiten /fr|se|nl/ranking/ mit AvaTrade auf Platz 1 (Kachel
    // ueber ACCOUNT_MARKE). AvaTrade-Klicks von Microsoft-Besuchern laufen nur auf diesen Seiten ueber den Tracker.
    quellen: ["openai", "microsoft", "microsoft-noid"],
    // Wert je Full Sign-up aus ChatGPT (05.10.): AvaPartner-Export (Trader-Quote x 750 $, GP/MQ 350 $), nur CPA, x 0,53 fuer
    // ChatGPT-Verkehr (FTDs seit 15.09. gegen die Erwartung aus 8.700 AvaPartner-Kunden 2024-25 nach Land, Geraet und
    // Reifezeit; 80 %: 0,23-1,06) / 0,954 (Geraetemix der Historie) -> Desktop-Werte. iPhone 0,62x, Android 0,76x Desktop.
    // TW = Schaetzung ohne Historie. ES/UK/IE/CA zahlen keinen CPA.
    regWert: {
      werte: { fr: 24, nl: 31, de: 27, ch: 36, se: 25, at: 32, it: 33, au: 22, dk: 15, no: 8, gp: 15, mq: 23, re: 24, lu: 24, tw: 13,
               es: 0, uk: 0, gb: 0, ie: 0, ca: 0 },
      mitSpread: { fr: 33, nl: 45, de: 48, ch: 51, se: 50, at: 37, it: 33, au: 23, dk: 16, no: 8, gp: 33, mq: 30, re: 33, lu: 33, tw: 13 },
      standard: 0,
      geraet: { desktop: 1, ios: 0.62, android: 0.76, unknown: 0.8 },
      quelle: { microsoft: 1.89, "microsoft-noid": 1.89 },
      hinweis: "Full Sign-ups x Wert je Land x Gerät: historische Trader-Quote laut AvaPartner x 750 $ (Guadeloupe/Martinique 350 $), nur CPA, x 0,53 für ChatGPT-Verkehr (gemessen 05.10., Spanne 0,2–1,0); Microsoft-Klicks ohne diesen Abschlag. Gerät: Desktop 1, iPhone 0,62, Android 0,76. Faktor = Wert / Ausgaben der Marke.",
    },
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
      { key: "cpa", label: "Trader (CPA)", labelCompact: "Trader", short: "Trader", icon: "party" },
    ],
  },
  {
    /** BlackBull (Operator 2026-09-28): steht in der Conversions-Liste unter AvaTrade, an der Stelle von
     *  RoboForex. Postback /pb/blackbull, Stufen wie AvaTrade; BlackBull 2 zaehlt ueber den Slug-Praefix mit. */
    // Seit 2026-09-30 pausiert und aus dem Dashboard genommen (Operator); Tracking laeuft fuer Nachzuegler weiter.
    slug: "blackbull", label: "BlackBull", rank: 11.8, mergesAccounts: true, org: "C", hidden: true,   // Masterkonto C (Operator 2026-09-28)
    stages: [
      // Sign-up = fertige Cellxpert-Registration wie bei eToro/AvaTrade; Leads nur als Zusatzzeile (Operator 2026-09-29).
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup", vorstufe: { key: "lead", label: "Leads", short: "Lead" } },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
      { key: "cpa", label: "Trader (CPA)", labelCompact: "Trader", short: "Trader", icon: "party" },
    ],
  },
  { slug: "blackbull2", label: "BlackBull 2", rank: 11.9, org: "C", stages: [], hidden: true },
  // Masterkonto D (Operator 2026-09-30): je Produkt ein Werbekonto und eine eigene Spalte neben AvaTrade.
  // Seit 2026-10-02 pausiert und ausgeblendet: NordVPN, Pianoforall, Brain Training, HighLevel (Operator) - bei ChatGPT
  // laufen nur noch AvaTrade und Deel.
  // Weitere Konten derselben Marke zaehlen ueber den Slug-Praefix mit (nordvpn2 -> nordvpn2x).
  {
    slug: "nordvpn2", label: "NordVPN", rank: 12, mergesAccounts: true, org: "D", spalte: true, hidden: true,
    stages: [
      { key: "sale", label: "Sales", short: "Sale", icon: "sale", provision: true },
    ],
  },
  {
    /** NordVPN ueber Meta (Operator 2026-10-26): nordvpn.signup-here.com (fbclid-Weiche, Klick /clk/nordvpn) -> NordVPN-Partnerlink.
     *  Kampagne "NordVPN | Meta | 14 Laender" (7 Sprachgruppen Mobil+Desktop, 40 $/Tag), Ziel = NordVPN-Kauf (TUNE -> Tracker -> CAPI
     *  Purchase). Facebook-Kachel nur mit den NordVPN-Kampagnen; Conversions = Tracker, Quelle meta, Broker nordvpn. */
    slug: "nordvpn-meta", broker: "nordvpn", label: "NordVPN · Facebook", marke: "NordVPN · Facebook", rank: 12.05,
    spalte: true, meta: true, metaKampagne: /NordVPN/i, quellen: ["meta", "meta-noid"], hidden: true,   // Operator 2026-10-29: Kampagne pausiert, raus aus dem Dashboard
    stages: [
      { key: "sale", label: "Sales", short: "Sale", icon: "sale", provision: true },
    ],
  },
  {
    /** Deel (PartnerStack): Demo gebucht, qualifizierte Verkaufschance (500 $), Abschluss (1.000 $). */
    slug: "deel", label: "Deel", rank: 13, mergesAccounts: true, org: "D", spalte: true, reihe: 3, hidden: true,   // Operator 2026-10-24: pausiert, raus aus dem Dashboard
    stages: [
      { key: "registration", label: "Demos", short: "Demo", icon: "signup" },
      { key: "cpa", label: "Qualifiziert", labelCompact: "Qualif.", short: "SQO", icon: "party", provision: true },
      { key: "sale", label: "Abschlüsse", labelCompact: "Deals", short: "Deal", icon: "sale", provision: true },
    ],
  },
  {
    /** Pepperstone bei ChatGPT (Operator 2026-10-07): zwei Werbekonten auf /pepperstone-registration (Konto 2: ?k=2),
     *  Weiterleitung ueber impact.com (Programm 21941). Tracker-Broker pepperstone1/pepperstone2; nur ChatGPT-Klicks
     *  zaehlen (die alten Microsoft-Pepperstone-Daten heissen "pepperstone" und haben Quelle microsoft).
     *  Fuenfte Karte der unteren Reihe. Seit 2026-10-07 live in UK/AU/DE; FTD und Trader kommen aus dem impact-Ingest
     *  ("First Deposit", "Qualified Trade" = einzige bezahlte Stufe, Payout je Land). */
    slug: "pepperstone", label: "Pepperstone", rank: 13.7, mergesAccounts: true, spalte: true, reihe: 3, hidden: true,   // Operator 2026-10-24: pausiert, raus aus dem Dashboard
    // Wert je Sign-up (Analyse 07.10.): Quote Anmeldung -> Qualified Trade bei Microsoft-UK 2/36, x 0,3-0,55 fuer ChatGPT
    // (eToro/AvaTrade) = ~2,3 % x Vertrags-CPA (AU/DE/AE/GB 1.000 $, TH/TW/VN/HK 450 $, MX/CO 250 $, sonst 0). Desktop
    // etwas ueber dem Mittel, DE schwaecher (ChatGPT-DE konvertiert bei eToro/AvaTrade schlechter). Sehr duenne Basis.
    regWert: {
      werte: { uk: 25, gb: 25, au: 25, ae: 22, de: 18, th: 10, tw: 10, vn: 10, hk: 10, mx: 6, co: 6 },
      standard: 0,
      geraet: { desktop: 1, ios: 0.65, android: 0.4, unknown: 0.8 },
      hinweis: "Sign-ups x Wert je Land x Gerät: ~2,3 % der ChatGPT-Anmeldungen werden Qualified Trader (Microsoft-UK 2 von 36, x 0,3–0,55 für ChatGPT) x Vertrags-CPA (UK/AU/DE/AE 1.000 $). Sehr dünne Basis. Gerät: Desktop 1, iPhone 0,65, Android 0,4.",
    },
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
      { key: "cpa", label: "Trader (CPA)", labelCompact: "Trader", short: "Trader", icon: "party", provision: true },
    ],
  },
  {
    /** 1xBet (Operator 2026-10-08): Microsoft-Ads-Markenkampagnen "1X <CC> | brand" im Sammelkonto DL - $, Review-Seite
     *  /reviews/1xbet/ leitet Microsoft-Klicks auf 1xPartners (tag mit Land + Sub-ID). Conversions kommen aus dem
     *  1xPartners-Report (manuell eingespielt). Sechste Karte der unteren Reihe. */
    slug: "1xbet", label: "1xBet", rank: 1.4, spalte: true, allSources: true, hidden: true,   // Operator 2026-11-05: raus aus dem Dashboard (Kampagnen seit 20.10. pausiert); rank 1.4: in Reihe 2 links (Operator 2026-10-09)
    msKonto: "138225870", msPrefix: "1X ", msKontoName: "DL - $",
    msZusatz: [{ konto: "189367589", name: "1xBet 2" }],   // Operator 2026-10-12: Kopie im Konto $ - b/c (Domain-Test winningwizzards)
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
    ],
  },
  {
    /** BetKing (KingMakers, Operator 2026-10-20): technisch wie 1xBet - Kampagne "BK NG | brand (20.10)" im Sammelkonto DL - $,
     *  betking.signup-here.com -> top10 /reviews/betking/ -> KingMakers-Link (Sub-ID in t1/s1). Regs/FTDs aus dem stuendlichen
     *  Abruf des KingMakers-Portals (Spielerdaten dort ~1 Tag verzoegert). Rechts neben 1xBet. */
    slug: "betking", label: "BetKing", rank: 1.45, spalte: true, allSources: true, hidden: true,   // Operator 2026-10-24: pausiert, raus aus dem Dashboard
    msKonto: "138225870", msPrefix: "BK ", msKontoName: "DL - $",
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
    ],
  },
  {
    /** XTB ueber ChatGPT Ads (Operator 2026-10-23): xtb.signup-here.com (englische Registrierungs-Anleitung, Weiche fuer oppref)
     *  -> top10 /xtb-review/ (Weiche + Tracker-Klick, Broker xtb) -> XTB-Geolink geolink.xtb.com/Yvz8p. Der Geolink nimmt keine
     *  Sub-ID an und XTB dokumentiert keinen Postback: Conversions kommen erst ueber /pb/xtb bzw. einen Import. Seit 2026-10-23
     *  abends drei Werbekonten xtb1..3 (Kacheln unten, Klick-Broker xtb<k> ueber ?k= auf /xtb-review/). Reihe 3. */
    slug: "xtb", label: "XTB · ChatGPT", marke: "XTB · ChatGPT", rank: 13.75, spalte: true, reihe: 3, quellen: ["openai"],
    hidden: true,   // Operator 2026-10-30: alle XTB-Kampagnen pausiert, raus aus dem Dashboard
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
      { key: "cpa", label: "Trader (CPA)", labelCompact: "Trader", short: "Trader", icon: "party" },
    ],
  },
  /** Die drei XTB-Werbekonten (Operator 2026-10-23 abends, Kontoname "XTB", Ziel-URL xtb.signup-here.com/?k=1..3): der Tracker
   *  nennt deren Klicks xtb1..3 (brokerPasst "xtb" fasst sie in der Karte zusammen), Ausgaben/Klicks je Konto aus OPENAI_ACCOUNTS. */
  { slug: "xtb1", label: "XTB · 1", rank: 13.751, stages: [], hidden: true },
  { slug: "xtb2", label: "XTB · 2", rank: 13.752, stages: [], hidden: true },
  { slug: "xtb3", label: "XTB · 3", rank: 13.753, stages: [], hidden: true },
  {
    /** ElevenLabs (Operator 2026-10-07): Microsoft-Ads-Markenkampagnen "EL <CC> | brand (8.10)" im Sammelkonto DL - $,
     *  Review-Seiten /elevenlabs-review/<lang>/ leiten Microsoft-Klicks auf den PartnerStack-Link. Anmeldung = PartnerStack
     *  customer.created, Kunde = erste Provision eines Kunden, Umsatz = alle Provisionen (22 % der Abo-Zahlungen, 12 Monate).
     *  Alle Quellen: auch Anmeldungen ueber die Buttons der Review-Seite (ohne Klick-ID). Sechste Karte der unteren Reihe. */
    // Seit 2026-10-08 pausiert und aus dem Dashboard genommen (Operator); Tracking laeuft fuer Nachzuegler weiter.
    slug: "elevenlabs", label: "ElevenLabs", rank: 13.8, spalte: true, allSources: true, hidden: true,
    msKonto: "138225870", msPrefix: "EL ", msKontoName: "DL - $",
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "Kunden", short: "Kunde", icon: "deposit" },
      { key: "sale", label: "Umsatz", short: "Umsatz", icon: "sale", provision: true },
    ],
  },
  {
    /** ChatGPT-Kampagnen auf die Top-10-Vergleichsseite (Operator 2026-10-06): zwei Werbekonten "Top 10 1/2", der Besucher
     *  klickt von der Vergleichsseite zu eToro oder AvaTrade. Der Tracker nennt diese Klicks und Conversions
     *  <ziel>vgl<konto> (etorovgl1, avatradevgl2; ohne erkennbares Konto etorovgl/avatradevgl) - sie zaehlen NUR hier,
     *  nie in der eToro- oder AvaTrade-Karte (`brokerPasst`). Alle Quellen: den vgl-Broker vergibt der Tracker nur bei
     *  Klickquelle openai. Ausgaben/Klicks = Konten etorovgl1-10 (avatradevgl1-10 sind im Tracker nur Aliase
     *  derselben Werbekonten, ohne eigene Kampagnen). Vierte Karte der unteren Reihe (rank nach Deel). */
    slug: "top10vgl", label: "OpenAI · Top 10 Vergleich", rank: 13.5, spalte: true, reihe: 3, allSources: true,
    vergleich: [
      { marke: "etoro", label: "eToro", praefix: "etorovgl" },
      { marke: "avatrade", label: "AvaTrade", praefix: "avatradevgl" },
    ],
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
      { key: "cpa", label: "Trader (CPA)", labelCompact: "Trader", short: "Trader", icon: "party" },
    ],
  },
  /** Die beiden Werbekonten der Vergleichsseite (Masterkonto D, Kontoname "Top 10 Trading Platforms"). */
  { slug: "etorovgl1", label: "Top 10 · 1", rank: 13.6, stages: [] },
  { slug: "etorovgl2", label: "Top 10 · 2", rank: 13.7, stages: [] },
  /** Top 10 · 3-6 (Operator 2026-10-19, Masterkonto C, Kontoname "Top 10 Trading Platform"): gleicher Aufbau, t10k=3..6. */
  { slug: "etorovgl3", label: "Top 10 · 3", rank: 13.71, stages: [] },
  { slug: "etorovgl4", label: "Top 10 · 4", rank: 13.72, stages: [] },
  { slug: "etorovgl5", label: "Top 10 · 5", rank: 13.73, stages: [] },
  { slug: "etorovgl6", label: "Top 10 · 6", rank: 13.74, stages: [] },
  /** Top 10 · 7-10 (Operator 2026-10-23, Kontoname "Top 10 Trading Platforms", Konto-URL topten.signup-here.com): Kopie von 3-6,
   *  Anzeigen-URL https://topten.signup-here.com/?t10k=7..10 (Weiche nach Land auf die Laenderseiten). */
  { slug: "etorovgl7", label: "Top 10 · 7", rank: 13.75, stages: [] },
  { slug: "etorovgl8", label: "Top 10 · 8", rank: 13.76, stages: [] },
  { slug: "etorovgl9", label: "Top 10 · 9", rank: 13.77, stages: [] },
  { slug: "etorovgl10", label: "Top 10 · 10", rank: 13.78, stages: [] },
  {
    /** ClickBank (Operator 2026-09-30): Erstkauf (an OpenAI) und Upsells (nur Tracker), je mit Provision. */
    slug: "pianoforall", label: "Pianoforall", rank: 15, mergesAccounts: true, org: "D", spalte: true, hidden: true,
    stages: [
      { key: "sale", label: "Verkäufe", short: "Sale", icon: "sale", provision: true },
      { key: "upsell", label: "Upsells", short: "Upsell", icon: "sale", provision: true },
    ],
  },
  {
    slug: "braintraining", label: "Brain Training", rank: 16, mergesAccounts: true, org: "D", spalte: true, hidden: true,
    stages: [
      { key: "sale", label: "Verkäufe", short: "Sale", icon: "sale", provision: true },
      { key: "upsell", label: "Upsells", short: "Upsell", icon: "sale", provision: true },
    ],
  },
  {
    /** HighLevel (FirstPromoter): Testphase gestartet (Leads = nur E-Mail als Zusatzzeile), erste Zahlung,
     *  Provisionen (40 % wiederkehrend). */
    slug: "gohighlevel", label: "HighLevel", rank: 14, mergesAccounts: true, org: "D", spalte: true, hidden: true,
    stages: [
      { key: "registration", label: "Trials", short: "Trial", icon: "signup", vorstufe: { key: "lead", label: "Leads", short: "Lead" } },
      { key: "ftd", label: "Kunden", short: "Kunde", icon: "deposit" },
      { key: "sale", label: "Provisionen", labelCompact: "Prov.", short: "Prov.", icon: "sale", provision: true },
    ],
  },
  {
    slug: "nordvpn", label: "NordVPN", rank: 20, hidden: true,
    stages: [
      { key: "sale", label: "Sales", short: "Sale", icon: "sale", provision: true },
    ],
  },
  {
    slug: "shopify", label: "Shopify", rank: 21, hidden: true,   // pausiert 2026-09-12, Operator: raus aus dem Dashboard
    stages: [
      { key: "registration", label: "Free Trials", short: "Trial", icon: "signup" },
      { key: "sale", label: "Paid Shops", short: "Shop", icon: "sale", provision: true },
    ],
  },
  {
    /** Bluehost (Operator 2026-11-02): Microsoft-Ads-Markenkampagnen "BH <Gebiet> | brand (02.11)" im Sammelkonto DL - $,
     *  bluehost.signup-here.com leitet Microsoft-Klicks auf den impact.com-Link (subId1 = Tracker-Klick-ID); Trials/Sales
     *  kommen aus dem impact-Ingest (alle Quellen). Karte wie 1xBet: Kampagnen + Conversions. */
    slug: "bluehost", label: "Bluehost", rank: 1.46, spalte: true, allSources: true, hidden: true,   // Operator 2026-11-02 abends: Bluehost-Kampagnen pausiert, raus aus dem Dashboard
    msKonto: "138225870", msPrefix: "BH ", msKontoName: "DL - $",
    stages: [
      { key: "sale", label: "Sales", short: "Sale", icon: "sale", provision: true },
      { key: "registration", label: "Trials", short: "Trial", icon: "signup", hideIfZero: true },
    ],
  },
  {
    /** Farmers Insurance (Operator 2026-11-02): Microsoft-Ads-Markenkampagne "FI US | brand (02.11)" im Sammelkonto DL - $,
     *  farmers.signup-here.com leitet Microsoft-Klicks auf den impact.com-Link (Programm 43423, zahlt je Angebotsanfrage);
     *  Quotes = Tracker-Stufe registration aus dem impact-Ingest. Karte wie 1xBet. */
    slug: "farmers", label: "Farmers Insurance", rank: 1.47, spalte: true, allSources: true, hidden: true,   // Operator 2026-11-05: Kampagne pausiert, raus aus dem Dashboard
    msKonto: "138225870", msPrefix: "FI ", msKontoName: "DL - $",
    stages: [
      { key: "registration", label: "Quotes", short: "Quote", icon: "signup", provision: true },
    ],
  },
  {
    /** Coach EU (Operator 2026-11-03): Microsoft-Markenkampagne "CO EU | brand (03.11)" im Sammelkonto DL - $ (je Land eine
     *  Anzeigengruppe), coach.signup-here.com leitet Microsoft-Klicks auf den impact.com-Link des Landes (Programm 52133, 6 % vom
     *  Bestellwert); Verkaeufe = Tracker-Stufe sale aus dem impact-Ingest. Karte wie 1xBet. */
    slug: "coach", label: "Coach", rank: 1.48, spalte: true, allSources: true, hidden: true,   // Operator 2026-11-05: Kampagne pausiert, raus aus dem Dashboard
    msKonto: "138225870", msPrefix: "CO ", msKontoName: "DL - $",
    stages: [
      { key: "sale", label: "Sales", short: "Sale", icon: "sale", provision: true },
    ],
  },
  {
    /** GPSAnyone (Operator 2026-11-03): MaxBounty-Angebot (28 $ je bezahltem erstem Monat nach dem 0,99-$-Test), Microsoft-
     *  Suchkampagne "GP US-UK-CA-AU | leads (03.11)" im Sammelkonto DL - $ (generische "track phone by number"-Keywords);
     *  gpsanyone.signup-here.com leitet Microsoft-Klicks auf den MaxBounty-Link (s2 = Klick-ID); Sales = Tracker-Stufe sale aus
     *  dem MaxBounty-Postback /pb/maxbounty. Karte wie Coach. */
    slug: "gpsanyone", label: "GPSAnyone", rank: 1.491, spalte: true, allSources: true, hidden: true,   // Operator 2026-11-05: pausiert, raus aus dem Dashboard; 04.11.-05.11. live: "GP US | leads (04.11)", Direct Linking ueber Tracker /t/gpsanyone -> MaxBounty (Offer 33390)
    msKonto: "138225870", msPrefix: "GP ", msKontoName: "DL - $",
    stages: [
      { key: "sale", label: "Sales", short: "Sale", icon: "sale", provision: true },
    ],
  },
  {
    /** Oojo (Operator 2026-11-03): MaxBounty-Angebot (15 $ je internationaler Flugbuchung, US/CA/UK), Microsoft-Suchkampagne
     *  "OJ US-CA-UK | leads (03.11)" in DL - $ (generische "international flights"-Keywords, kein Brand-Bidding);
     *  oojo.signup-here.com leitet Microsoft-Klicks auf den MaxBounty-Link des Landes; Bookings = Tracker-Stufe sale. */
    slug: "oojo", label: "Oojo", rank: 1.492, spalte: true, allSources: true, hidden: true,   // Operator 2026-11-05: pausiert, raus aus dem Dashboard; 04.11.-05.11. live: "OJ US | leads (04.11)", Direct Linking ueber Tracker /t/oojo -> MaxBounty
    msKonto: "138225870", msPrefix: "OJ ", msKontoName: "DL - $",
    stages: [
      { key: "sale", label: "Bookings", short: "Booking", icon: "sale", provision: true },
    ],
  },
  {
    /** Medicare Plan Help Desk (Operator 2026-11-03): Pay-per-Call ueber DOPPCALL, Microsoft-Suchkampagne "MD US | calls (03.11)"
     *  in DL - $ (Mo-Fr 9-19 ET); medicare-plans.signup-here.com zeigt Microsoft-Klicks die Auto-Dial-Seite (tel:). Bezahlte
     *  Anrufe = Tracker-Stufe registration aus dem DOPPCALL-Postback /pb/calls (ohne Klick-ID, nur Zaehlung + Provision). */
    slug: "medicare", label: "Medicare Calls", rank: 1.493, spalte: true, allSources: true, hidden: true,   // Operator 2026-11-03 abends: Kampagne noch pausiert (Freigabe ausstehend), raus aus dem Dashboard
    msKonto: "138225870", msPrefix: "MD ", msKontoName: "DL - $",
    stages: [
      { key: "registration", label: "Calls", short: "Call", icon: "signup", provision: true },
    ],
  },
  {
    /** Flights Booking Desk (Operator 2026-11-03): Pay-per-Call ueber MarketCall (18 $ ab 60 s), Microsoft-Suchkampagne
     *  "FL US | calls (03.11)" in DL - $ (taeglich 6-23 ET, generische Flug-Keywords, keine Airline-Marken);
     *  flights.signup-here.com zeigt Microsoft-Klicks die Auto-Dial-Seite (tel:). Bezahlte Anrufe = Stufe registration aus /pb/calls. */
    slug: "flights", label: "Flights Calls", rank: 1.494, spalte: true, allSources: true, hidden: true,   // Operator 2026-11-05: pausiert, raus aus dem Dashboard; 04.11.-05.11. live: "FL US | calls (03.11)", MarketCall-Nummer (866) 228-6323, nur Smartphones, 8-20 Uhr ET
    msKonto: "138225870", msPrefix: "FL ", msKontoName: "DL - $",
    stages: [
      { key: "registration", label: "Qualified Calls", short: "Call", icon: "signup", provision: true, vorstufe: { key: "call", label: "Anrufe", short: "Anruf" } },
    ],
  },
  {
    /** Water Damage Help Desk (Operator 2026-11-03): Pay-per-Call ueber MarketCall ("Water Damage and Mold Remediation Bundle",
     *  dynamische Auszahlung je PLZ, Tier 1 64-1.280 $), Microsoft-Suchkampagne "WD US | calls (03.11)" in DL - $ (nur Smartphones);
     *  waterdamage.signup-here.com zeigt Microsoft-Klicks die Auto-Dial-Seite (tel: 888-576-3071). Bezahlte Anrufe = Stufe
     *  registration aus /pb/calls (broker waterdamage). */
    slug: "waterdamage", label: "Water Damage Calls", rank: 1.495, spalte: true, allSources: true, hidden: true,   // Operator 2026-11-05: Kampagne pausiert, raus aus dem Dashboard
    msKonto: "138225870", msPrefix: "WD ", msKontoName: "DL - $",
    // Seit 04.11.2026 aus der MarketCall-API (reconcile/marketcall_ingest.py): Calls = qualifizierte Anrufe (Hold/Approved) mit
    // Auszahlung, Zusatzzeile "Anrufe" = alle Anrufe (Stufe call).
    stages: [
      { key: "registration", label: "Qualified Calls", short: "Call", icon: "signup", provision: true, vorstufe: { key: "call", label: "Anrufe", short: "Anruf" } },
    ],
  },
  {
    /** Palmera (Operator 2026-11-03): Dubai-Immobilien-Partnerprogramm (30 % der Palmera-Provision, 180 Tage First-Touch).
     *  Microsoft-Suchkampagne "PM World | dubai (03.11)" im Sammelkonto DL - $ (je Land eine Anzeigengruppe, generische
     *  Keywords "property in dubai"); dubai.signup-here.com leitet Microsoft-Klicks auf die Palmera-Dubai-Liste mit ref=5GJXFW.
     *  Leads = Tracker-Stufe registration, Deals (ab Status Booked) = sale mit Provision, beides aus dem Palmera-Partner-API-Ingest
     *  (reconcile/palmera_ingest.py, Zuordnung zum Klick ueber Zeit + Land). Karte wie Coach. */
    slug: "palmera", label: "Palmera", rank: 1.49, spalte: true, hidden: true,   // Operator 2026-11-05: Kampagne pausiert, raus aus dem Dashboard
    msKonto: "138225870", msPrefix: "PM ", msKontoName: "DL - $",
    // Seit 2026-11-04 (Operator) eigene Zeile "Palmera · Google" darunter: hier alles ausser Google-Klicks (Leads ohne
    // zugeordneten Klick bleiben hier, Google-Leads tragen immer eine gclid).
    quellenAusser: ["google", "google-noid"],
    // Reihenfolge = Spalten der Partneruebersicht (Reg. / FTD / Trader): Leads zaehlen als Reg., Deals als FTD (Operator 2026-11-04).
    stages: [
      { key: "registration", label: "Leads", short: "Lead", icon: "signup" },
      { key: "sale", label: "Deals", short: "Deal", icon: "sale", provision: true },
    ],
  },
  {
    /** Palmera ueber Google (Operator 2026-11-04): 4 PMax US/GB/IN/AE im Konto 2649797403 "Atarax - Dubai Property", eigene Zeile
     *  direkt unter der Microsoft-Zeile. Leads = Palmera-Leads mit unserer gclid (Partner-API), Deals = Abschluesse davon. */
    slug: "palmera-google", broker: "palmera", label: "Palmera · Google", marke: "Palmera · Google", rank: 1.4901, spalte: true, hidden: true,   // Operator 2026-11-05: Kampagne pausiert, raus aus dem Dashboard
    googleKonto: "palmera", quellen: ["google", "google-noid"],
    stages: [
      { key: "registration", label: "Leads", short: "Lead", icon: "signup" },
      { key: "sale", label: "Deals", short: "Deal", icon: "sale", provision: true },
    ],
  },
  {
    /** Seit 2026-09-21 mit eigenen Stufen (Operator): Sign-ups und FTDs aus dem Tracker,
     *  Tagesvolumen aus den Provisions-Tickets. Die Partner-API kennt zwar keine Klick-ID,
     *  aber `has_reached_deposit_threshold` je Konto — daraus kommt der FTD. Statt „Trader
     *  (CPA)" steht hier das Volumen: RoboForex Infinity zahlt nach Lots, nicht pro Trader.
     *  Seit 2026-09-28 NICHT mehr in der Conversions-Liste (Operator: BlackBull statt RoboForex,
     *  RoboForex-Kampagnen sind pausiert). Kacheln bleiben; Stufen zum Wiedereinblenden:
     *  registration/ftd aus dem Tracker. */
    slug: "roboforex", label: "RoboForex", rank: 10, mergesAccounts: true, quellen: ["openai", "microsoft", "microsoft-noid"],
    // Seit 2026-10-01 wieder eine Spalte (Microsoft-Relaunch, je Land eine Kampagne "RF <CC> | …" in DL - $).
    // Umsatz wird nicht angezeigt (Operator 2026-10-02).
    spalte: true, reihe: 3, msKonto: "138225870", msPrefix: "RF ", msKontoName: "DL - $",
    stages: [
      { key: "registration", label: "Sign-ups", short: "Reg", icon: "signup" },
      { key: "ftd", label: "FTDs", short: "FTD", icon: "deposit" },
    ],
  },
  /** Masterkonto B: zweites AvaTrade-Werbekonto (Landing /avatrade-registration-2/), eigenes
   *  Pixel. Keine eigenen Stufen — seine Conversions zaehlen unter AvaTrade mit, es gibt nur
   *  ein AvaTrade-Partnerkonto (Operator 2026-09-15). Eigene Kachel behaelt es. */
  { slug: "avatrade2", label: "AvaTrade 2", rank: 12, org: "B", stages: [] },
  /** Ehemals eToro 6 (Masterkonto A), seit 2026-09-22 AvaTrade-Konto mit Landing
   *  /avatrade-registration-5/; Tracker-Schluessel seit 2026-09-24 `avatrade5` statt `etoro6`. */
  { slug: "avatrade5", label: "AvaTrade 5", rank: 11.5, stages: [] },
  /** Masterkonto B: zweites RoboForex-Werbekonto (Landing /account-registration-2/). Wie das
   *  erste ohne Conversion-Stufen — die Partner-API kennt keine Klick-ID. */
  { slug: "roboforex2", label: "RoboForex 2", rank: 13, org: "B", stages: [] },
  /** Masterkonto C (ab 2026-09-21): erscheinen, sobald ihre Keys in OPENAI_ACCOUNTS stehen. */
  { slug: "etoro17", label: "eToro 17", rank: 17, org: "C", stages: [] },
  { slug: "etoro18", label: "eToro 18", rank: 18, org: "C", stages: [] },
  { slug: "avatrade3", label: "AvaTrade 3", rank: 19, org: "C", stages: [] },
  { slug: "avatrade4", label: "AvaTrade 4", rank: 20, org: "C", stages: [] },
  /** Masterkonto C, seit 2026-09-29: Landing /avatrade-registration-6/ bzw. -7/. */
  { slug: "avatrade6", label: "AvaTrade 6", rank: 20.3, org: "C", stages: [] },
  { slug: "avatrade7", label: "AvaTrade 7", rank: 20.6, org: "C", stages: [] },
  { slug: "roboforex3", label: "RoboForex 3", rank: 21, org: "C", stages: [] },
  { slug: "roboforex4", label: "RoboForex 4", rank: 22, org: "C", stages: [] },
  { slug: "pepperstone1", label: "Pepperstone 1", rank: 23, stages: [], hidden: true },
  { slug: "pepperstone2", label: "Pepperstone 2", rank: 23.5, stages: [], hidden: true },
];

export const productLabel = (slug: string): string =>
  PRODUCTS.find((p) => p.slug === slug)?.label ?? slug;

export const productRank = (slug: string): number =>
  PRODUCTS.find((p) => p.slug === slug)?.rank ?? 99;

const STAGE_SHORT_DEFAULT: Record<string, string> = { registration: "Reg", ftd: "FTD", cpa: "Trader", lead: "Lead" };

export const stageShort = (slug: string, stage: string): string =>
  PRODUCTS.find((p) => p.slug === slug)?.stages.find((s) => s.key === stage)?.short ?? STAGE_SHORT_DEFAULT[stage] ?? stage;

export const productHidden = (slug: string): boolean =>
  PRODUCTS.find((p) => p.slug === slug)?.hidden === true;

/** OpenAI-Organisation des Kontos; ohne Angabe gehört es zur ersten ("A"). */
export const productOrg = (slug: string): Org =>
  PRODUCTS.find((p) => p.slug === slug)?.org ?? "A";

/** Broker der Top-10-Vergleichsseite bei ChatGPT: <ziel>vgl<konto> (etorovgl1, avatradevgl2, ohne Konto etorovgl). */
export const istVergleichBroker = (broker: string): boolean => /^[a-z]+vgl\d*$/.test(broker);

/** Gehoert ein Tracker-Broker (Conversion oder Werbekonto) zu einem Marken-Praefix? Wie bisher ueber den Slug-Praefix
 *  (etoro12 -> etoro, avatrade5 -> avatrade), aber Broker der Vergleichsseite (etorovgl1 beginnt auch mit "etoro") nur
 *  zu einem Vergleichs-Praefix - sonst landeten sie in der eToro- bzw. AvaTrade-Karte (Operator 2026-10-06). */
export const brokerPasst = (broker: string, praefix: string): boolean =>
  broker.startsWith(praefix) && (istVergleichBroker(praefix) || !istVergleichBroker(broker));

/** Kontonummer eines Vergleichs-Brokers ("etorovgl1" -> "1", "avatradevgl" -> ""), sonst null. avatradevgl1 und
 *  etorovgl1 sind dasselbe Werbekonto (Tracker: alias_of). */
export const vergleichKonto = (broker: string): string | null =>
  istVergleichBroker(broker) ? broker.replace(/^[a-z]+vgl/, "") : null;
